import clsx from 'clsx';
import React, { memo, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';

import { PrimaryButton } from '@components/Button/variants';
import { NewsletterProps } from '@components/pages/Article/interfaces';
import useEventTracking from '@hooks/useEventTracking';
import useSubscribeToNewsletter from '@hooks/useSubscribeToNewsletter';
import { newsletter_list_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';

const BUTTON_TEXT = {
  SUBSCRIBE_NOW: 'Subscribe now',
  SUBSCRIBED: 'Subscribed',
  VIEW_LIST: 'View list',
};
const buttonClassName = 'px-32 !text-button-md max-sm:!w-fit mt-8';

const SubscribeToNewsletter = ({
  description,
  frequency,
  id,
  isSubscribed,
  name,
}: NewsletterProps): JSX.Element => {
  const { trackEvent } = useEventTracking({ location: TrackingLocation.SubscribeToNewsletter });
  const [successfullySubscribed, setSuccessfullySubscribed] = useState(false);
  const { mutate: subscribeToNewsletter, isLoading: isSubscribeLoading } =
    useSubscribeToNewsletter();
  const subscribeButtonText = successfullySubscribed
    ? BUTTON_TEXT.SUBSCRIBED
    : BUTTON_TEXT.SUBSCRIBE_NOW;

  const handleSubscribe = () => {
    try {
      subscribeToNewsletter({ newsletter_id: id });
      setSuccessfullySubscribed(true);
      trackEvent(click({ clickTarget: ClickTarget.Subscribe, clickText: subscribeButtonText }));
    } catch (err) {
      setSuccessfullySubscribed(false);
    }
  };

  return (
    <aside className="relative mt-80 print:hidden md:-mt-[168px]" data-testid="newsletter-article">
      <span className="block font-suisse-intl-mono text-overline-sm text-grayscale-gray">
        {frequency}
      </span>
      <h3 className="mb-0 mt-8 font-suisse-works text-headline-xxs text-black">{name}</h3>
      <p className="my-0 font-suisse-intl text-button-xs font-light text-grayscale-gray">
        {ReactHtmlParser(description)}
      </p>
      {isSubscribed ? (
        <PrimaryButton
          as="link"
          size="small"
          className={buttonClassName}
          href={newsletter_list_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.ViewList }))}
        >
          {BUTTON_TEXT.VIEW_LIST}
        </PrimaryButton>
      ) : (
        <PrimaryButton
          as="button"
          size="small"
          className={clsx(buttonClassName, {
            'disabled:bg-white disabled:text-grayscale-gray': successfullySubscribed,
          })}
          onClick={handleSubscribe}
          disabled={isSubscribeLoading || successfullySubscribed}
        >
          {subscribeButtonText}
        </PrimaryButton>
      )}
    </aside>
  );
};

export default memo(SubscribeToNewsletter);
